import {
	IDataListSortParams,
	Id,
	LaravelResponse,
	PaginatedListFetcherConfig,
	keyToUrl,
	mapPaginatedFetcherConfigToAxiosConfig,
	withDestroyEndpoint,
	withIndexEndpoint,
	withOptionsEndpoint,
	withStoreEndpoint,
	withUpdateEndpoint,
} from "@dgs/core";
import axios from "axios";
import { TenantIndexResource } from "../../../../shared/types/tenant";
import {
	ContactCheckEmailResource,
	ContactCreateRequest,
	IContactBaseInfoRequest,
	IContactCompanyRequest,
	IContactIndexResource,
	IContactOptionResource,
	IContactUpdateRequest,
	IContactUpdateResource,
} from "../types";
import { ArchiveByTenantRequest } from "../types/archive";

const baseKey = "contacts";
const showKey = (id: Id) => [baseKey, `${id}`];

const keys = {
	baseKey: [baseKey],
	export: [baseKey, "export"],
	indexArchived: [baseKey, "archived"],
	listArchived: [baseKey, "archived", "list"],
	attachCompany: (contactId: Id, companyId: Id) => [baseKey, `${contactId}`, "companies", `${companyId}`, "attach"],
	detachCompany: (contactId: Id, companyId: Id) => [baseKey, `${contactId}`, "companies", `${companyId}`, "detach"],
	markCompanyAsPrimary: (contactId: Id, companyId: Id) => [
		baseKey,
		`${contactId}`,
		"companies",
		`${companyId}`,
		"change-primary",
	],
	deleteAddress: (contactId: Id, addressId: Id) => [baseKey, `${contactId}`, "addresses", `${addressId}`],
	deleteEmailAddress: (contactId: Id, emailAddressId: Id) => [
		baseKey,
		`${contactId}`,
		"email-addresses",
		`${emailAddressId}`,
	],
	deletePhoneNumber: (contactId: Id, phoneNumberId: Id) => [
		baseKey,
		`${contactId}`,
		"phone-numbers",
		`${phoneNumberId}`,
	],
	checkEmail: (email: string) => [baseKey, "check-email", email],
};

const urls = {
	export: keyToUrl(keys.export),
	attachCompany: (contactId: Id, companyId: Id) => keyToUrl(keys.attachCompany(contactId, companyId)),
	detachCompany: (contactId: Id, companyId: Id) => keyToUrl(keys.detachCompany(contactId, companyId)),
	markCompanyAsPrimary: (contactId: Id, companyId: Id) => keyToUrl(keys.markCompanyAsPrimary(contactId, companyId)),
	deleteAddress: (contactId: Id, addressId: Id) => keyToUrl(keys.deleteAddress(contactId, addressId)),
	deleteEmailAddress: (contactId: Id, emailId: Id) => keyToUrl(keys.deleteEmailAddress(contactId, emailId)),
	deletePhoneNumber: (contactId: Id, phoneId: Id) => keyToUrl(keys.deletePhoneNumber(contactId, phoneId)),
	checkEmail: keyToUrl([baseKey, "check-email"]),
};

const updateTags = (contactId: Id, tagIds: Id[]) => axios.put(`contacts/${contactId}/tags`, { tagIds });

const updateBaseInfo = (contactId: Id, values: IContactBaseInfoRequest) =>
	axios.put<LaravelResponse<IContactUpdateResource>>(`contacts/${contactId}/base-info`, values);

const attachCompany = (contactId: Id, companyId: Id, contactCompany: IContactCompanyRequest) =>
	axios.put(urls.attachCompany(contactId, companyId), contactCompany);

const detachCompany = (contactId: Id, companyId: Id) => axios.put(urls.detachCompany(contactId, companyId));

const markCompanyAsPrimary = (companyId: Id, contactId: Id) =>
	axios.put(urls.markCompanyAsPrimary(contactId, companyId));

const deleteAddress = (contactId: Id, addressId: Id) => axios.delete(urls.deleteAddress(contactId, addressId));

const deleteEmailAddress = (contactId: Id, emailId: Id) => axios.delete(urls.deleteEmailAddress(contactId, emailId));

const deletePhoneNumber = (contactId: Id, phoneId: Id) => axios.delete(urls.deletePhoneNumber(contactId, phoneId));

const indexActiveWithContactFilter = (
	config: PaginatedListFetcherConfig<IContactIndexResource>,
	contactFilterId: Id | null,
	contactViewId: Id | null,
) => {
	const axiosConfig = mapPaginatedFetcherConfigToAxiosConfig(config);

	return axios.get<LaravelResponse<IContactIndexResource[]>>(keyToUrl([baseKey]), {
		...axiosConfig,
		params: {
			...axiosConfig.params,
			sort: config.sortParams
				? {
						contactAttributeId: config.sortParams.column,
						direction: config.sortParams.direction,
					}
				: null,
			contactFilterId,
			contactViewId,
		},
	});
};
const indexArchivedWithContactFilter = (
	config: PaginatedListFetcherConfig<IContactIndexResource>,
	contactFilterId: Id | null,
	contactViewId: Id | null,
) => {
	const axiosConfig = mapPaginatedFetcherConfigToAxiosConfig(config);

	return axios.get<LaravelResponse<IContactIndexResource[]>>(keyToUrl(keys.indexArchived), {
		...axiosConfig,
		params: {
			...axiosConfig.params,
			sort: config.sortParams
				? {
						contactAttributeId: config.sortParams.column,
						direction: config.sortParams.direction,
					}
				: null,
			contactFilterId,
			contactViewId,
		},
	});
};
const postExport = (
	isArchived: boolean,
	contactViewId?: Id,
	contactFilterId?: Id | null,
	search?: string | null,
	sort?: IDataListSortParams<IContactIndexResource> | null,
) =>
	axios.post(urls.export, {
		isArchived: isArchived ? "1" : "0",
		contactFilterId,
		contactViewId,
		search,
		sort: sort
			? {
					contactAttributeId: sort.column,
					direction: sort.direction,
				}
			: null,
	});

const checkEmail = function (email: string) {
	return axios.get<ContactCheckEmailResource>(urls.checkEmail, { params: { email } }).then((response) => {
		if (response.status === 200) {
			return response.data;
		} else {
			return null;
		}
	});
};

export const contactService = {
	keys,
	urls,
	updateTags,
	updateBaseInfo,
	attachCompany,
	detachCompany,
	markCompanyAsPrimary,
	deleteAddress,
	deleteEmailAddress,
	deletePhoneNumber,
	indexActiveWithContactFilter,
	indexArchivedWithContactFilter,
	postExport,
	checkEmail,
	...withIndexEndpoint<IContactIndexResource>([baseKey]),
	...withOptionsEndpoint<IContactOptionResource>([baseKey]),
	...withUpdateEndpoint<IContactUpdateRequest>([baseKey]),
	...withStoreEndpoint<ContactCreateRequest>([baseKey]),
	...withDestroyEndpoint([baseKey]),
	show: (id: Id, contactViewId?: Id) =>
		axios.get<LaravelResponse<IContactUpdateResource>>(keyToUrl(showKey(id)), {
			params: { contactViewId: contactViewId },
		}),
	showKey,
	indexArchivedKey: [baseKey, "archived"],
	getArchivedTenantsKey: (companyId: Id) => [baseKey, `${companyId}`, "archived-tenants"],
	getArchivedTenants: (companyId: Id) => {
		return axios.get<LaravelResponse<TenantIndexResource[]>>(keyToUrl([baseKey, `${companyId}`, "archived-tenants"]));
	},
	getTenantsKey: (companyId: Id) => [baseKey, `${companyId}`, "tenants"],
	getTenants: (companyId: Id) => {
		return axios.get<LaravelResponse<TenantIndexResource[]>>(keyToUrl([baseKey, `${companyId}`, "tenants"]));
	},
	archive: (companyId: Id, request: ArchiveByTenantRequest) =>
		axios.put(keyToUrl([baseKey, `${companyId}`, "archive"]), request),
	restore: (companyId: Id, request: ArchiveByTenantRequest) =>
		axios.put(keyToUrl([baseKey, `${companyId}`, "restore"]), request),
};
